import React, { useState } from 'react';
import { Select } from 'antd';
import { debounce } from 'lodash';
import { UseQueryResult } from 'react-query';

import { SearchQueryRequestType } from '../../common/types';

import { FormFieldLabel } from './FormFieldLabel';
import { StyledFormItem } from './FormInput';

export type OptionType = {
  value: string | number;
  content?: React.ReactNode;
};

export type FormSelectProps = {
  name: string;
  label?: string;
  allowClear?: boolean;
  options: OptionType[] | [];
  required?: boolean;
  mode?: 'multiple' | 'tags';
  onChange?: (option: OptionType) => void;
};

type FormSelectWithSearch<T> = Omit<FormSelectProps, 'options' | 'onChange'> & {
  search: (query: SearchQueryRequestType | undefined) => UseQueryResult<{
    count: number;
    results: T[];
  }>;
  mapFormValueToPayload: (newValue: string) => SearchQueryRequestType;
  mapResponseToOptions: (items: T[]) => OptionType[];
};

export function FormSelectWithSearch<T>({
  name,
  label,
  search,
  allowClear = true,
  required,
  mapFormValueToPayload,
  mapResponseToOptions,
}: FormSelectWithSearch<T>) {
  const [payload, setPayload] = useState<SearchQueryRequestType>(mapFormValueToPayload(''));
  const { data: searchData } = search(payload);
  const [value, setValue] = useState('');

  const handleSearch = React.useRef(
    debounce(
      (newValue: string) => {
        const newPayload = mapFormValueToPayload(newValue);
        setPayload(newPayload);
      },
      500,
      {
        leading: false,
        trailing: true,
      }
    )
  ).current;

  const searchOptions = mapResponseToOptions(searchData?.results || []);

  return (
    <StyledFormItem name={name} label={<FormFieldLabel required={required}>{label}</FormFieldLabel>}>
      <Select
        showSearch
        allowClear={allowClear}
        options={searchOptions}
        filterOption={false}
        value={value}
        onChange={(newValue) => setValue(newValue)}
        onSearch={handleSearch}
      />
    </StyledFormItem>
  );
}
